import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function Auth() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [regForm, setRegForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);

  const doLogin = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const u = await login(loginForm.email, loginForm.password);
      toast.success(`Welcome back, ${u.name}`);
      navigate(u.role === 'admin' ? '/admin' : '/account');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally { setBusy(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await register(regForm.email, regForm.password, regForm.name);
      toast.success('Account created');
      navigate('/account');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/account';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="px-4 sm:px-8 max-w-md mx-auto py-20">
      <div className="text-center mb-10">
        <span className="font-script text-5xl text-gold">Ke-Sa</span>
        <h1 className="font-serif text-3xl mt-4">Welcome back</h1>
        <p className="text-sm text-muted-foreground mt-2">Sign in to save favourites, reserve pieces and schedule delivery.</p>
      </div>

      <Tabs defaultValue="login">
        <TabsList className="grid grid-cols-2 w-full bg-surface">
          <TabsTrigger value="login" data-testid="tab-login">Sign In</TabsTrigger>
          <TabsTrigger value="register" data-testid="tab-register">Create Account</TabsTrigger>
        </TabsList>

        <TabsContent value="login">
          <form onSubmit={doLogin} className="space-y-5 mt-6">
            <div><Label className="text-xs">Email</Label>
              <Input data-testid="auth-email-input" type="email" required value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} className="mt-1.5 bg-surface border-border" /></div>
            <div><Label className="text-xs">Password</Label>
              <Input data-testid="auth-password-input" type="password" required value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} className="mt-1.5 bg-surface border-border" /></div>
            <button data-testid="auth-submit-button" disabled={busy} className="w-full bg-gold text-primary-foreground py-3.5 text-xs font-mono uppercase tracking-widest hover:bg-gold-hover disabled:opacity-50">
              {busy ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        </TabsContent>

        <TabsContent value="register">
          <form onSubmit={doRegister} className="space-y-5 mt-6">
            <div><Label className="text-xs">Full Name</Label>
              <Input data-testid="reg-name-input" required value={regForm.name} onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} className="mt-1.5 bg-surface border-border" /></div>
            <div><Label className="text-xs">Email</Label>
              <Input data-testid="reg-email-input" type="email" required value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} className="mt-1.5 bg-surface border-border" /></div>
            <div><Label className="text-xs">Password</Label>
              <Input data-testid="reg-password-input" type="password" required value={regForm.password} onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} className="mt-1.5 bg-surface border-border" /></div>
            <button data-testid="reg-submit-button" disabled={busy} className="w-full bg-gold text-primary-foreground py-3.5 text-xs font-mono uppercase tracking-widest hover:bg-gold-hover disabled:opacity-50">
              {busy ? 'Creating…' : 'Create Account'}
            </button>
          </form>
        </TabsContent>
      </Tabs>

      <div className="flex items-center gap-4 my-8">
        <div className="flex-1 h-px bg-border" /><span className="text-xs text-muted-foreground font-mono">OR</span><div className="flex-1 h-px bg-border" />
      </div>

      <button data-testid="auth-google-login-button" onClick={googleLogin} className="w-full border border-border py-3.5 text-sm hover:border-gold hover:text-gold transition-colors flex items-center justify-center gap-3">
        <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Continue with Google
      </button>
    </div>
  );
}
